import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Button, Field, Heading, Input, Text, VStack, chakra } from '@chakra-ui/react'
import { useUpdateGrantProfile } from '../../../hooks/useGrantProfile'
import { EMAIL_ERROR, PHONE_ERROR, isValidEmail, isValidPhone } from '../../../lib/contactMethods'
import { extractApiMessage } from '../../../lib/apiError'
import type { GrantProfile } from '../../../types/grant'

interface ProfileDetailsFormProps {
  profile: GrantProfile | undefined
  isOffline: boolean
}

type FieldErrors = Partial<Record<'first' | 'last' | 'phone' | 'email', string>>

/**
 * [Canvasser redesign Phase 7, P7-8] The canvasser's own name / phone / email. Online-only and says so.
 * The local checks are for instant feedback only; the server is authoritative and its 400 text is shown.
 * Fields seed only when a NEW profile arrives while the form is untouched: a refetch never clobbers typing,
 * and a successful save never snaps back to the pre-save cache (the hook writes the saved values into the
 * cache first; the later refetch reseeds with the server's normalised values). Phone/email the canvasser
 * never touched go up as null (= unchanged, P7-8).
 */
export function ProfileDetailsForm({ profile, isOffline }: ProfileDetailsFormProps) {
  const updateMutation = useUpdateGrantProfile()
  const update = updateMutation.mutate
  const [first, setFirst] = useState(profile?.firstName ?? '')
  const [last, setLast] = useState(profile?.lastName ?? '')
  const [phone, setPhone] = useState(profile?.phone ?? '')
  const [email, setEmail] = useState(profile?.email ?? '')
  // Refs, not state: changing them must not re-run the seeding effect (that re-run is what snapped fields back).
  const dirty = useRef(false)
  const touched = useRef({ phone: false, email: false })
  const [errors, setErrors] = useState<FieldErrors>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (!profile || dirty.current) return
    setFirst(profile.firstName)
    setLast(profile.lastName)
    setPhone(profile.phone ?? '')
    setEmail(profile.email ?? '')
  }, [profile])

  const edit = (set: (v: string) => void, v: string, field?: 'phone' | 'email') => {
    set(v)
    dirty.current = true
    if (field) touched.current[field] = true
    setSaved(false)
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const next: FieldErrors = {}
    if (!first.trim()) next.first = 'Enter your first name'
    if (!last.trim()) next.last = 'Enter your last name'
    if (phone.trim() && !isValidPhone(phone)) next.phone = PHONE_ERROR
    if (email.trim() && !isValidEmail(email)) next.email = EMAIL_ERROR
    setErrors(next)
    setServerError(null)
    setSaved(false)
    if (Object.keys(next).length > 0) return
    update(
      {
        firstName: first.trim(),
        lastName: last.trim(),
        phone: touched.current.phone ? phone.trim() : null,
        email: touched.current.email ? email.trim() : null,
      },
      {
        onSuccess: () => {
          setSaved(true)
          dirty.current = false
          touched.current = { phone: false, email: false }
        },
        onError: err => setServerError(extractApiMessage(err, "Couldn't save your details.")),
      },
    )
  }

  return (
    <chakra.form onSubmit={onSubmit} noValidate borderWidth="1px" borderColor="border" borderRadius="lg" bg="surface" p={4}>
      <VStack align="stretch" gap={3}>
        <Heading size="sm">Your details</Heading>
        <Field.Root invalid={!!errors.first}>
          <Field.Label>First name</Field.Label>
          <Input minH="48px" maxLength={100} autoComplete="given-name" value={first} onChange={e => edit(setFirst, e.target.value)} />
          <Field.ErrorText>{errors.first}</Field.ErrorText>
        </Field.Root>
        <Field.Root invalid={!!errors.last}>
          <Field.Label>Last name</Field.Label>
          <Input minH="48px" maxLength={100} autoComplete="family-name" value={last} onChange={e => edit(setLast, e.target.value)} />
          <Field.ErrorText>{errors.last}</Field.ErrorText>
        </Field.Root>
        <Field.Root invalid={!!errors.phone}>
          <Field.Label>Phone (optional)</Field.Label>
          <Input minH="48px" maxLength={32} type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={e => edit(setPhone, e.target.value, 'phone')} />
          <Field.ErrorText>{errors.phone}</Field.ErrorText>
        </Field.Root>
        <Field.Root invalid={!!errors.email}>
          <Field.Label>Email (optional)</Field.Label>
          <Input minH="48px" maxLength={254} type="email" inputMode="email" autoComplete="email" value={email} onChange={e => edit(setEmail, e.target.value, 'email')} />
          <Field.ErrorText>{errors.email}</Field.ErrorText>
        </Field.Root>
        <Button type="submit" colorPalette="brand" minH="48px" disabled={isOffline} loading={updateMutation.isPending}>
          Save
        </Button>
        {isOffline && <Text color="fg.muted" fontSize="sm">Connect to the internet to edit your details.</Text>}
        {saved && <Text color="fg.muted" fontSize="sm">Saved</Text>}
        {serverError && <Text color="danger.fg">{serverError}</Text>}
      </VStack>
    </chakra.form>
  )
}
